/**
 * Universal Game Template
 * 
 * A flexible, comprehensive template that defines the structure and default configuration
 * for all scratch card games. This ensures consistency across both predefined and custom games.
 */

export const UNIVERSAL_GAME_TEMPLATE = {
  // Core layout and structure (consistent across all games)
  layout: {
    type: "three_column", // Fixed layout with card, controls, and info columns
    cardColumn: {
      width: { base: "100%", md: "auto" }
    },
    controlsColumn: {
      width: { base: "100%", md: "400px" }
    },
    infoColumn: {
      width: { base: "100%", md: "300px" }
    }
  },
  
  // Card configuration (customizable)
  card: {
    grid: {
      rows: 3,
      columns: 3
    },
    symbols: {
      winning: "⭐",
      default: "?"
    }
  },
  
  // Visual theme (customizable)
  theme: {
    colors: {
      primary: "#1b2e43", // Main background
      secondary: "#0d1f3b", // Containers
      accent: "yellow.400", // Highlights
      text: "white",
      cellBackground: "#ffc107"
    },
    glassmorphism: {
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      border: "1px solid rgba(255, 255, 255, 0.15)"
    }
  },
  
  // Game features (toggleable)
  features: {
    bountyPool: true,
    lottery: false,
    giftOption: true,
    revealAll: true,
    recentWinners: true
  },
  
  // Prize configuration (customizable)
  prizes: {
    tiers: [
      { amount: 25000, count: 1 },
      { amount: 5000, count: 5 },
      { amount: 1000, count: 10 }
    ],
    tokenType: "JINU"
  }
};

/**
 * Predefined color themes for games
 */
export const GAME_THEMES = {
  cashKing: {
    colors: {
      primary: "#1b432e", // Green theme
      secondary: "#0d3b1f",
      accent: "yellow.400",
      text: "white",
      cellBackground: "#ffc107"
    },
    glassmorphism: {
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      border: "1px solid rgba(0, 255, 128, 0.15)"
    }
  },
  jackpotMillions: {
    colors: {
      primary: "#2e1b43", // Purple theme
      secondary: "#1f0d3b",
      accent: "purple.400",
      text: "white",
      cellBackground: "#9c27b0"
    },
    glassmorphism: {
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      border: "1px solid rgba(128, 0, 255, 0.15)"
    }
  },
  vegasTop: {
    colors: {
      primary: "#8B0000", // Red theme
      secondary: "#590000",
      accent: "yellow.300",
      text: "white",
      cellBackground: "#e91e63"
    },
    glassmorphism: {
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      border: "1px solid rgba(255, 0, 0, 0.15)"
    }
  },
  oceanBlue: {
    colors: {
      primary: "#1b2e43", // Blue theme
      secondary: "#0d1f3b",
      accent: "cyan.400",
      text: "white",
      cellBackground: "#2196f3"
    },
    glassmorphism: {
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      border: "1px solid rgba(0, 128, 255, 0.15)"
    }
  }
};

/**
 * Helper function to merge a custom configuration with the universal template
 * Safely handles undefined or incomplete inputs
 */
export const mergeWithTemplate = (customConfig) => {
  // If customConfig is undefined or null, use an empty object
  const safeConfig = customConfig || {};
  
  // Create a deep merged configuration
  return {
    ...UNIVERSAL_GAME_TEMPLATE,
    ...safeConfig,
    // Ensure ID is always present
    id: safeConfig.id || `game-${Date.now()}`,
    // Ensure title and description are always present
    title: safeConfig.title || 'Custom Game',
    description: safeConfig.description || 'A custom scratch card game',
    // Merge layout properties
    layout: {
      ...UNIVERSAL_GAME_TEMPLATE.layout,
      ...(safeConfig.layout || {})
    },
    // Merge card properties
    card: {
      ...UNIVERSAL_GAME_TEMPLATE.card,
      ...(safeConfig.card || {}),
      // Ensure grid is always present
      grid: {
        ...UNIVERSAL_GAME_TEMPLATE.card.grid,
        ...(safeConfig.card?.grid || {})
      },
      // Ensure symbols are always present
      symbols: {
        ...UNIVERSAL_GAME_TEMPLATE.card.symbols,
        ...(safeConfig.card?.symbols || {})
      }
    },
    // Merge theme properties
    theme: {
      ...UNIVERSAL_GAME_TEMPLATE.theme,
      ...(safeConfig.theme || {}),
      // Ensure colors are always present
      colors: {
        ...UNIVERSAL_GAME_TEMPLATE.theme.colors,
        ...((safeConfig.theme && safeConfig.theme.colors) || {})
      },
      // Ensure glassmorphism properties are always present
      glassmorphism: {
        ...UNIVERSAL_GAME_TEMPLATE.theme.glassmorphism,
        ...((safeConfig.theme && safeConfig.theme.glassmorphism) || {})
      }
    },
    // Merge features
    features: {
      ...UNIVERSAL_GAME_TEMPLATE.features,
      ...(safeConfig.features || {})
    },
    // Merge prizes
    prizes: {
      ...UNIVERSAL_GAME_TEMPLATE.prizes,
      ...(safeConfig.prizes || {}),
      // Ensure tiers are always present
      tiers: safeConfig.prizes?.tiers || UNIVERSAL_GAME_TEMPLATE.prizes.tiers
    },
    // Ensure publishedAt is always present
    publishedAt: safeConfig.publishedAt || new Date().toISOString(),
    // Ensure status is always present
    status: safeConfig.status || 'PUBLISHED'
  };
};

/**
 * Convert legacy game config to universal format
 * Safely handles edge cases and incomplete data
 */
export const convertToUniversalFormat = (legacyConfig) => {
  // Handle null or undefined input
  if (!legacyConfig) return mergeWithTemplate({});
  
  // Check if it's already in universal format
  if (legacyConfig.theme && legacyConfig.theme.colors && 
      legacyConfig.card && legacyConfig.card.grid) {
    // Still merge with template to ensure all properties exist
    return mergeWithTemplate(legacyConfig);
  }
  
  // Extract values from legacy config with safe defaults
  const {
    id = `game-${Date.now()}`,
    title = 'Custom Game',
    description = 'A custom scratch card game',
    baseConfig = {},
    visualCustomization = {},
    layout = {},
    gameLogic = {},
    prizeTiers = [],
    hasLottery = false,
    publishedAt = new Date(),
    status = 'PUBLISHED'
  } = legacyConfig;
  
  // Determine primary color based on available data
  let primaryColor = "#1b2e43"; // Default blue
  if (visualCustomization.backgroundColor) {
    primaryColor = visualCustomization.backgroundColor;
  } else if (baseConfig.bgColor) {
    primaryColor = baseConfig.bgColor;
  } else if (visualCustomization.cardColor) {
    primaryColor = visualCustomization.cardColor;
  }
  
  // Determine secondary color (darker shade of primary)
  let secondaryColor = "#0d1f3b"; // Default darker blue
  if (visualCustomization.secondaryColor) {
    secondaryColor = visualCustomization.secondaryColor;
  } else if (primaryColor === "#8B0000" || primaryColor === "red.700") {
    secondaryColor = "#590000"; // Darker red
  } else if (primaryColor.includes("purple") || primaryColor.includes("#44")) {
    secondaryColor = "#1f0d3b"; // Darker purple
  } else if (primaryColor.includes("green") || primaryColor.includes("#06")) {
    secondaryColor = "#064e3b"; // Darker green
  }
  
  // Determine border color based on primary color
  let borderColor = "rgba(0, 128, 255, 0.15)"; // Default blue border
  if (primaryColor === "#8B0000" || primaryColor === "red.700") {
    borderColor = "rgba(255, 0, 0, 0.15)"; // Red border
  } else if (primaryColor.includes("purple") || primaryColor.includes("#44")) {
    borderColor = "rgba(128, 0, 255, 0.15)"; // Purple border
  } else if (primaryColor.includes("green") || primaryColor.includes("#06")) {
    borderColor = "rgba(0, 255, 128, 0.15)"; // Green border
  }
  
  // Create universal format config
  const universalConfig = {
    id,
    title,
    description,
    layout: {
      ...UNIVERSAL_GAME_TEMPLATE.layout,
      cardColumn: {
        width: { base: "100%", md: "auto" }
      },
      controlsColumn: {
        width: { base: "100%", md: "400px" }
      },
      infoColumn: {
        width: { base: "100%", md: "300px" }
      }
    },
    card: {
      grid: {
        rows: layout.gridSize?.rows || layout.rows || 3,
        columns: layout.gridSize?.columns || layout.columns || 3
      },
      symbols: {
        winning: gameLogic.circleSymbol || visualCustomization.symbol || "⭐",
        default: gameLogic.gridSymbol || "?"
      }
    },
    theme: {
      colors: {
        primary: primaryColor,
        secondary: secondaryColor,
        accent: visualCustomization.accentColor || baseConfig.accentColor || "yellow.400",
        text: visualCustomization.textColor || "white",
        cellBackground: visualCustomization.cellColor || baseConfig.gridBoxBackground || "#ffc107"
      },
      glassmorphism: {
        backdropFilter: "blur(10px)",
        boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
        border: `1px solid ${borderColor}`
      }
    },
    features: {
      ...UNIVERSAL_GAME_TEMPLATE.features,
      lottery: hasLottery
    },
    prizes: {
      tiers: prizeTiers.length > 0 ? 
        prizeTiers.map(tier => ({
          amount: parseFloat(tier.amount || "0") || 0,
          count: parseInt(tier.count || "1") || 1
        })) : 
        UNIVERSAL_GAME_TEMPLATE.prizes.tiers,
      tokenType: (prizeTiers[0]?.tokenType || baseConfig.tokenType || "JINU")
    },
    publishedAt,
    status
  };
  
  // Merge with template to ensure all properties exist
  return mergeWithTemplate(universalConfig);
};
